import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

import { API_BASE_URL } from "@/config";
import {
  BROWSE_PAGE,
  BROWSE_WALK_PAGE,
  isLiveTable,
  isPriceVisibility,
  isPublicBrowseGames,
  type PublicBrowseGames,
  type PublicLiveTable,
  type PublicPriceVisibility,
} from "@/hooks/usePublicBrowse";
import { browseFacetParams, type BrowseFilters, type BrowseSort } from "@/lib/browse/browseFilter";
import {
  fetchCursorPage,
  fetchFirstPage,
  nextPlannedCursor,
  type PlannedCursor,
  type PlannedPage,
  priceIntent,
  priceParams,
  usePriceBoundsKey,
  useServerPriceKnowledge,
  useServerPriceSort,
  walks,
} from "@/lib/browse/serverPriceSort";
import { isRecord, isStringOrNull, optionalFields, validatedJson } from "@/lib/ui/validatedJson";

export type PublicInventoryItem = {
  public_id: string;
  stock_group_public_id: string;
  name: string;
  image_url: string;
  qty?: number;
  product_type?: string | null;
  game?: string | null;
  set_name?: string | null;
  number?: string | null;
  variant?: string | null;
  condition?: string | null;
  is_graded?: boolean;
  grading_company?: string | null;
  grade?: string | null;
  rarity?: string | null;
  ask?: string | null;
  currency?: string | null;
};

export type PublicInventory = {
  slug: string;
  display_name: string;
  buylist_published?: boolean;
  shop_logo_url?: string | null;
  // The front-door identity ADR-0137 publishes, all optional: an older response, or a shop that
  // has filled none of it in, renders exactly what it did before.
  tagline?: string;
  bio?: string;
  operating_area?: string;
  tags?: string[];
  links?: { label: string; url: string; }[];
  // The tables this vendor is running right now (ADR-0138), which the front door hosts under a
  // tab. Empty or absent means no live table and no tab bar — the shop is the whole page.
  live_tables?: PublicLiveTable[];
  stock_groups: { public_id: string; name: string; }[];
  items: PublicInventoryItem[];
  // The keyset envelope (ADR-0016), all optional because an API that predates paging sends the
  // whole shop in one page without them.
  total?: number;
  shop_total?: number;
  next_cursor?: string | null;
  has_more?: boolean;
  // Which price the cards show; "none" for a shop that shows no prices (Q15). Absent on an API that
  // predates it.
  price_visibility?: PublicPriceVisibility;
  // How fresh the stored prices are, and whether the server orders and bounds by them (TRO-2345).
  // Paged responses only, and absent on an API that predates them.
  priced_as_of?: string | null;
  prices_changed_at?: string | null;
  server_price_sort?: boolean;
  /** The facets the server narrowed in SQL (TRO-2347); absent from an older server. */
  server_facets?: string[];
};

/** The pages loaded so far as one inventory: the first page's envelope over every loaded item.
 *  `total` is the units matching the search and facets, `shop_total` every listed unit. */
export type LoadedPublicInventory = PublicInventory & { total: number; shop_total: number; };

function isPublicInventoryItem(value: unknown): value is PublicInventoryItem {
  return isRecord(value) && typeof value.public_id === "string"
    && typeof value.stock_group_public_id === "string" && typeof value.name === "string"
    && typeof value.image_url === "string"
    && (value.qty === undefined || typeof value.qty === "number")
    && optionalFields(
      value,
      [
        "product_type",
        "game",
        "set_name",
        "number",
        "variant",
        "condition",
        "grading_company",
        "grade",
        "rarity",
        "ask",
        "currency",
      ],
      isStringOrNull,
    )
    && (value.is_graded === undefined || typeof value.is_graded === "boolean");
}

export function isPublicInventory(value: unknown): value is PublicInventory {
  return isRecord(value) && typeof value.slug === "string"
    && typeof value.display_name === "string" && Array.isArray(value.stock_groups)
    && (value.buylist_published === undefined || typeof value.buylist_published === "boolean")
    && (value.total === undefined || typeof value.total === "number")
    && (value.shop_total === undefined || typeof value.shop_total === "number")
    && (value.next_cursor === undefined || isStringOrNull(value.next_cursor))
    && optionalFields(
      value,
      ["shop_logo_url", "tagline", "bio", "operating_area", "priced_as_of", "prices_changed_at"],
      isStringOrNull,
    )
    && (value.server_price_sort === undefined || typeof value.server_price_sort === "boolean")
    && (value.server_facets === undefined
      || (Array.isArray(value.server_facets)
        && value.server_facets.every((facet) => typeof facet === "string")))
    && (value.price_visibility === undefined || isPriceVisibility(value.price_visibility))
    && (value.tags === undefined
      || (Array.isArray(value.tags) && value.tags.every((tag) => typeof tag === "string")))
    && (value.live_tables === undefined
      || (Array.isArray(value.live_tables) && value.live_tables.every(isLiveTable)))
    && (value.links === undefined
      || (Array.isArray(value.links)
        && value.links.every((link) =>
          isRecord(link) && typeof link.label === "string" && typeof link.url === "string"
        )))
    && value.stock_groups.every((stockGroup) =>
      isRecord(stockGroup) && typeof stockGroup.public_id === "string"
      && typeof stockGroup.name === "string"
    )
    && Array.isArray(value.items) && value.items.every(isPublicInventoryItem);
}

/**
 * Fetches a vendor's storefront one keyset page at a time, as `usePublicBrowse` does a table's.
 *
 * A plain `fetch()` with no init argument, so no auth header is ever sent: the page is public.
 * `search` and the column facets (`browseFacetParams`) run on the server, and so does name order.
 * Price order and the price range are the server's where it reports `server_price_sort`
 * (`serverPriceSort`, TRO-2346), and so are game and set where it lists them in `server_facets`
 * (TRO-2347). Elsewhere the hook walks the rest of the shop `BROWSE_WALK_PAGE` at a time before
 * the client orders or filters it, as main did.
 *
 * Against an API that predates paging, a page with no `next_cursor` ends the walk and a missing
 * `total` or `shop_total` reads as the item count.
 */
export function usePublicInventory(
  slug: string | null,
  search = "",
  sort: BrowseSort = "featured",
  filters: BrowseFilters = {},
) {
  const q = search.trim();
  const known = useServerPriceKnowledge();
  // The game and set facets go to the server once one of its pages listed them (TRO-2347); the
  // query key carries what is sent, so learning it mid-session reloads under them.
  const served = known.serverFacets ?? [];
  const facets = browseFacetParams(filters, served);
  const intent = priceIntent(sort, filters);
  const queryKey = ["public-inventory", slug, q, sort, facets, usePriceBoundsKey(known, intent)];
  const query = useInfiniteQuery({
    queryKey,
    enabled: !!slug,
    initialPageParam: undefined as PlannedCursor | undefined,
    placeholderData: keepPreviousData,
    queryFn: async ({ pageParam }): Promise<PlannedPage<PublicInventory>> => {
      const request = (plan: PlannedCursor["plan"], cursor?: string) => {
        const limit = cursor && walks(plan, intent, served) ? BROWSE_WALK_PAGE : BROWSE_PAGE;
        const qs = new URLSearchParams({ limit: String(limit) });
        if (cursor) qs.set("cursor", cursor);
        if (q) qs.set("q", q);
        for (const [key, value] of priceParams(plan, intent)) qs.set(key, value);
        for (const [key, value] of facets) qs.append(key, value);
        return fetch(`${API_BASE_URL}/shops/${encodeURIComponent(slug!)}?${qs.toString()}`);
      };
      const parse = (response: Response) =>
        validatedJson(response, isPublicInventory, "Public inventory response was invalid");
      const fail = (response: Response): never => {
        throw new Error(
          response.status === 404 ? "Inventory not found" : "Couldn't load inventory",
        );
      };
      return pageParam
        ? fetchCursorPage(
          known,
          pageParam.plan,
          () => request(pageParam.plan, pageParam.cursor),
          parse,
          fail,
        )
        : fetchFirstPage(known, intent, (plan) => request(plan), parse, fail);
    },
    getNextPageParam: nextPlannedCursor,
  });
  const priced = useServerPriceSort(known, queryKey, query, intent, served);
  const { walking } = priced;

  const {
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isPlaceholderData,
    isRefetching,
    fetchNextPage,
  } = query;
  const pages = query.data?.pages;
  const loadedPages = pages?.length ?? 0;
  // `sort` is in the query key, but `total` and `shop_total` are not ordered, so a sort-only change
  // serves a placeholder page whose counts are already right. Remember which narrowing they count.
  const countKey = JSON.stringify([q, facets, intent.minCents, intent.maxCents]);
  const countedKey = useRef(countKey);
  if (!isPlaceholderData) countedKey.current = countKey;
  useEffect(() => {
    // Stop on a page error rather than retry-looping the public endpoint. `loadedPages` re-runs
    // this after each page, even when a fast response left the fetching flag unchanged. A refetch
    // (the "Prices updated" pill) is left to finish: fetching a page would cancel it.
    if (walking && hasNextPage && !isFetchingNextPage && !isFetchNextPageError && !isRefetching) {
      void fetchNextPage();
    }
  }, [
    walking,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isRefetching,
    fetchNextPage,
    loadedPages,
  ]);

  const data = useMemo<LoadedPublicInventory | undefined>(() => {
    if (!pages || pages.length === 0) return undefined;
    const first = pages[0].page;
    const items = pages.flatMap((planned) => planned.page.items);
    return {
      ...first,
      items,
      total: first.total ?? items.length,
      shop_total: first.shop_total ?? items.length,
    };
  }, [pages]);

  return {
    data,
    isLoading: query.isLoading,
    isPending: query.isPending,
    error: query.error,
    refetch: query.refetch,
    // True while later pages load, or while `data` is still the previous query's answer as a newly
    // typed one is in flight, so the screen holds back a premature "no cards match".
    // A walk also counts between pages, before the effect above starts the next one; otherwise the
    // render in that gap shows a partial price order, then the grid empties again.
    isLoadingRest: isFetchingNextPage || isPlaceholderData
      || (walking && hasNextPage && !isFetchNextPageError),
    // Not react-query's flag: true only while the page on screen answers a *different* narrowing,
    // so its counts do not describe this one.
    countStale: isPlaceholderData && countedKey.current !== countKey,
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
    },
    // Whether this load walks the rest of the shop, so the screen holds a price-sorted grid only
    // then rather than blanking it each time a page loads on scroll.
    walking,
    // True when the server ordered these pages by price, so the client keeps their order.
    serverPriced: priced.plan === "server",
    // True when a price bound was not sent and the screen narrows the loaded cards by it, so it
    // counts them itself (on main's path, and at a shop that shows no prices).
    boundsOnClient: priced.plan !== "server",
    // The game and set facets this load sent, which the server narrowed (`server_facets`, TRO-2347).
    serverFacets: served,
    pricedAsOf: priced.pricedAsOf,
    pricesUpdated: priced.pricesUpdated,
    refreshPrices: priced.refreshPrices,
  };
}

/** Loads the storefront's complete game and set chips, which the paged list cannot supply. Its key
 *  sits under `public-inventory`, so signing out of a workspace leaves it alone. */
export function usePublicInventoryGames(slug: string | null) {
  const query = useQuery({
    queryKey: ["public-inventory", "games", slug],
    enabled: !!slug,
    queryFn: async (): Promise<PublicBrowseGames> => {
      const response = await fetch(`${API_BASE_URL}/shops/${encodeURIComponent(slug!)}/games`);
      if (!response.ok) throw new Error("Couldn't load this shop's game filters.");
      return await validatedJson(
        response,
        isPublicBrowseGames,
        "Game filters response was invalid",
      );
    },
  });
  return { games: query.data?.games ?? [], sets: query.data?.sets ?? [] };
}
